import router from '@/router';
import { defineStore } from 'pinia';
import { login, register, passwordReset, passwordSelect, passwordChange, updateUser } from '@/services/ppApi';
// import { useLanguageStore } from '@/stores/utils/language';
import { useAccountStore } from '@/stores/account';
import { gaEvent } from '@/utils/analytics/prospectpro';

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('pp_auth_token') || null,
    redirectAfterLogin: null,
    user: JSON.parse(localStorage.getItem('pp_user')) || null,
    searchHistory: JSON.parse(localStorage.getItem('pp_user_search_history')) || [],
    error: null,
    loading: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.token,
    hasUserDetails: (state) => !!state.user,
    isPartnerAdmin: (state) => !!state.user?.partner_role > 0,
    partnerRole: (state) => state.user?.partner_role || 100,
    isPartnerGod: (state) => !!state.user?.partner_role < 10 && ['olivier@bedrijfsdata.nl', 'peter@bedrijfsdata.nl', 'friso@bedrijfsdata.nl', 'olivier@prospectpro.nl', 'peter@prospectpro.nl', 'friso@prospectpro.nl'].includes(state.user.email.toLowerCase()),
  },
  actions: {
    setToken(token) {
      this.token = token;
      if (token) {
        localStorage.setItem('pp_auth_token', token);
      } else {
        localStorage.removeItem('pp_auth_token');
      }
    },
    setUser(updatedUser) {
        this.user = { ...this.user, ...updatedUser };
        if(updatedUser) {
            localStorage.setItem('pp_user', JSON.stringify(this.user));
        } else {
            localStorage.removeItem('pp_user');
        }
    },
    resetUser() {
        this.setToken(null);
        this.setUser(null);
        this.deleteAllOrderSettings();
        this.deleteAllSuspectSettings();
        this.deleteAllProspectsSettings();
        this.deleteAllLogsSettings();
        this.deleteAllCompaniesSettings();
        this.deleteAllProspectDetailsSettings();
        this.deleteSearchHistory();
        this.$reset();
    },
    reauthenticate() {
        this.resetUser();
        this.setRedirectAfterLogin(router.currentRoute.value.name || null);
        router.push({ name:'login'});
    },
    async login(email, password) {
        this.loading = true;
        this.error = null;

      try {
        const response = await login(email, password);
        this.setToken(response.data.token);
        this.setUser({
            email,
            name: response.data.name || null,
            uid: response.data.uid || null
        });

        // const languageStore = useLanguageStore();
        // const userPreferredLanguage = response.data.language;
        // if (userPreferredLanguage) {
        //   await languageStore.setLocale(userPreferredLanguage);
        // }

        if (response.data.default_account_id) {
            const accountStore = useAccountStore();
            const account = {
                account_id: response.data.default_account_id,
            };
            accountStore.setAccount(account);
        }

        if (response.data.partner_role && response.data.partner_role > 0) {
            this.setUser({
                partner_role: response.data.partner_role
            });
        }

        gaEvent('user_signin');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('user_signin', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async register(name, url, email, password) {
        this.loading = true;
        this.error = null;

        try {
            const response = await register(name, url, email, password);
            if (response.status === 200) {
                const loginSuccess = await this.login(email, password);
                gaEvent('user_registration');
                return loginSuccess;
            } else {
                gaEvent('user_registration', false);
                return false;
            }
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
            gaEvent('user_registration', false);
            return false;
        } finally {
            this.loading = false;
        }
    },
    async passwordChange(password, passwordNew, passwordConfirm) {
        this.loading = true;
        this.error = null;

        try {
          const response = await passwordChange(password, passwordNew, passwordConfirm);
          this.setToken(response.data.token);
          return true;
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
          return false;
        } finally {
          this.loading = false;
        }
    },
    async passwordReset(email) {
        this.loading = true;
        this.error = null;

      try {
        await passwordReset(email);
        gaEvent('password_reset');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('password_reset', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async passwordSelect(password, r) {
        this.loading = true;
        this.error = null;

      try {
        await passwordSelect(password, r);
        gaEvent('password_select');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('password_select', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updatePersonalia(user) {
        this.loading = true;
        this.error = null;

        try {
            const response = await updateUser(user);
            this.setUser(response.data.users[0]);
            return true;
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
            return false;
        } finally {
            this.loading = false;
        }
    },
    logout() {
        gaEvent('user_signout');
        this.resetUser();
        const accountStore = useAccountStore();
        accountStore.resetAccount();
        window.location.reload();
    },
    setRedirectAfterLogin(path = null) {
        if(path) {
            this.redirectAfterLogin = path;
        } else {
            this.redirectAfterLogin = null;
        }
    },
    redirectUserAfterLogin() {
      if (this.redirectAfterLogin) {
        router.push(this.redirectAfterLogin);
        this.redirectAfterLogin = null;
      } else {
        router.push({name: process.env.VUE_APP_DEFAULT_ROUTE});
      }
    },
    deleteAllSuspectSettings() {
        localStorage.removeItem('pp_suspect_settings');
    },
    getSuspectSettings(account_id = null) {
        if(!account_id) { return false; }
        const allSuspectSettings = JSON.parse(localStorage.getItem('pp_suspect_settings')) || null;
        if(allSuspectSettings && allSuspectSettings[account_id]) {
            return allSuspectSettings[account_id];
        } else {
             return false;
        }
    },
    saveSuspectSettings(account_id = null, suspect_settings = null) {
        if(!account_id) { return false; }
        var allSuspectSettings = JSON.parse(localStorage.getItem('pp_suspect_settings')) || {};
        if(suspect_settings) {
            allSuspectSettings[account_id] = suspect_settings
        } else if (allSuspectSettings && allSuspectSettings[account_id]) {
            allSuspectSettings[account_id] = null;
        }
        localStorage.setItem('pp_suspect_settings', JSON.stringify(allSuspectSettings));
    },
    deleteAllOrderSettings() {
        localStorage.removeItem('pp_order_settings');
    },
    getOrderSettings(account_id = null) {
        if(!account_id) { return false; }
        const allOrderSettings = JSON.parse(localStorage.getItem('pp_order_settings')) || null;
        if(allOrderSettings && allOrderSettings[account_id]) {
            return allOrderSettings[account_id];
        } else {
             return false;
        }
    },
    saveOrderSettings(account_id = null, order_settings = null) {
        if(!account_id) { return false; }
        var allOrderSettings = JSON.parse(localStorage.getItem('pp_order_settings')) || {};
        if(order_settings) {
            allOrderSettings[account_id] = order_settings
        } else if (allOrderSettings && allOrderSettings[account_id]) {
            allOrderSettings[account_id] = null;
        }
        localStorage.setItem('pp_order_settings', JSON.stringify(allOrderSettings));
    },
    deleteAllProspectsSettings() {
        localStorage.removeItem('pp_prospects_settings');
    },
    getProspectsSettings(account_id = null) {
        if(!account_id) { return false; }
        const allProspectsSettings = JSON.parse(localStorage.getItem('pp_prospects_settings')) || null;
        if(allProspectsSettings && allProspectsSettings[account_id]) {
            return allProspectsSettings[account_id];
        } else {
             return false;
        }
    },
    saveProspectsSettings(account_id = null, prospects_settings = null) {
        if(!account_id) { return false; }
        var allProspectsSettings = JSON.parse(localStorage.getItem('pp_prospects_settings')) || {};
        if(prospects_settings) {
            allProspectsSettings[account_id] = prospects_settings
        } else if (allProspectsSettings && allProspectsSettings[account_id]) {
            allProspectsSettings[account_id] = null;
        }
        localStorage.setItem('pp_prospects_settings', JSON.stringify(allProspectsSettings));
    },
    deleteAllLogsSettings() {
        localStorage.removeItem('pp_logs_settings');
    },
    getLogsSettings(account_id = null) {
        if(!account_id) { return false; }
        const allLogsSettings = JSON.parse(localStorage.getItem('pp_logs_settings')) || null;
        if(allLogsSettings && allLogsSettings[account_id]) {
            return allLogsSettings[account_id];
        } else {
             return false;
        }
    },
    saveLogsSettings(account_id = null, logs_settings = null) {
        if(!account_id) { return false; }
        var allLogsSettings = JSON.parse(localStorage.getItem('pp_logs_settings')) || {};
        if(logs_settings) {
            allLogsSettings[account_id] = logs_settings
        } else if (allLogsSettings && allLogsSettings[account_id]) {
            allLogsSettings[account_id] = null;
        }
        localStorage.setItem('pp_logs_settings', JSON.stringify(allLogsSettings));
    },
    deleteAllCompaniesSettings() {
        localStorage.removeItem('pp_companies_settings');
    },
    getCompaniesSettings(account_id = null) {
        if(!account_id) { return false; }
        const allCompaniesSettings = JSON.parse(localStorage.getItem('pp_companies_settings')) || null;
        if(allCompaniesSettings && allCompaniesSettings[account_id]) {
            return allCompaniesSettings[account_id];
        } else {
             return false;
        }
    },
    saveCompaniesSettings(account_id = null, settings = null) {
        if(!account_id) { return false; }
        var allCompaniesSettings = JSON.parse(localStorage.getItem('pp_companies_settings')) || {};
        if(settings) {
            allCompaniesSettings[account_id] = settings
        } else if (allCompaniesSettings && allCompaniesSettings[account_id]) {
            allCompaniesSettings[account_id] = null;
        }
        localStorage.setItem('pp_companies_settings', JSON.stringify(allCompaniesSettings));
    },
    deleteAllProspectDetailsSettings() {
        localStorage.removeItem('pp_prospect_details_settings');
    },
    getProspectDetailsSettings(account_id = null) {
        if(!account_id) { return false; }
        const allProspectDetailsSettings = JSON.parse(localStorage.getItem('pp_prospect_details_settings')) || null;
        if(allProspectDetailsSettings && allProspectDetailsSettings[account_id]) {
            return allProspectDetailsSettings[account_id];
        } else {
             return false;
        }
    },
    saveProspectDetailsSettings(account_id = null, settings = null) {
        if(!account_id) { return false; }
        var allProspectDetailsSettings = JSON.parse(localStorage.getItem('pp_prospect_details_settings')) || {};
        if(settings) {
            allProspectDetailsSettings[account_id] = settings
        } else if (allProspectDetailsSettings && allProspectDetailsSettings[account_id]) {
            allProspectDetailsSettings[account_id] = null;
        }
        localStorage.setItem('pp_prospect_details_settings', JSON.stringify(allProspectDetailsSettings));
    },
    addToSearchHistory(company) {
        if(!company?.id || !company?.name) {
            return;
        }

        const id = this.isAuthenticated ? company.id : company.ch ? (company.id + '_' + company.ch) : company.id;
        this.searchHistory = this.searchHistory.filter(item => item.id !== id);

        if (this.searchHistory.length >= 8) {
            this.searchHistory.pop();
        }

        this.searchHistory.unshift({
            id: id,
            name: company.name,
            domain: company.domain || null
        });

        localStorage.setItem('pp_user_search_history', JSON.stringify(this.searchHistory));
    },
    deleteSearchHistory() {
        localStorage.removeItem('pp_user_search_history');
    },
  }
});